import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import axiosRetry from "axios-retry";

// Types for our auth context
export interface User {
  sub: string;
  email: string;
  username: string;
  emailVerified: boolean;
}

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  cognitoRefreshToken: string;
  expiresIn: number;
}

// Extended request config with retry flag
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Create axios instance
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "",
  timeout: 10000,
  withCredentials: true, // Enable cookies for cross-origin requests
  headers: {
    "Content-Type": "application/json",
  },
});

// Configure retry logic
axiosRetry(apiClient, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error: AxiosError) => {
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || error.response?.status === 502;
  },
});

// Global auth state - will be managed by AuthContext
let globalAccessToken: string | null = null;
let refreshTokenFn: (() => Promise<void>) | null = null;
let globalErrorHandler: ((message: string) => void) | null = null;

export const setAuthToken = (token: string | null) => {
  globalAccessToken = token;
};

export const setRefreshFunction = (fn: (() => Promise<void>) | null) => {
  refreshTokenFn = fn;
};

export const setGlobalErrorHandler = (fn: ((message: string) => void) | null) => {
  globalErrorHandler = fn;
};

// Export functions for tRPC client to access shared auth state
export const getAuthToken = (): string | null => {
  return globalAccessToken;
};

export const getRefreshFunction = (): (() => Promise<void>) | null => {
  return refreshTokenFn;
};

// Request interceptor - attach access token
apiClient.interceptors.request.use(
  async config => {
    if (globalAccessToken) {
      config.headers.Authorization = `Bearer ${globalAccessToken}`;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle 401s and errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Handle 401 - try to refresh token once
    // BUT exclude refresh endpoint itself to prevent infinite loops
    if (
      error.response?.status === 401 &&
      refreshTokenFn &&
      originalRequest &&
      !(originalRequest as RetryableRequestConfig)._retry
    ) {
      (originalRequest as RetryableRequestConfig)._retry = true;

      try {
        await refreshTokenFn();
        // Retry the original request with new token
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login will be handled by AuthContext
        return Promise.reject(refreshError);
      }
    }

    // Show global error toast for network/server errors
    if (globalErrorHandler && !originalRequest?.url?.includes("/auth.")) {
      const status = error.response?.status;

      if (error.code === "NETWORK_ERROR" || !(globalThis.navigator?.onLine ?? true)) {
        globalErrorHandler("Network error. Please check your connection and try again.");
      } else if (status && status >= 500) {
        globalErrorHandler("Server error. Please try again later.");
      } else if (status === 429) {
        globalErrorHandler("Too many requests. Please wait a moment and try again.");
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
